import { Pause, Play } from 'lucide-react';
import { useEffect, useState } from 'react';
import SigninWithGoogle from '../assets/google-logo.svg';
import BannerImage from '../assets/location-station-banner.svg';
import { User, useUser } from '../contexts/UserContext';
import axios from 'axios';
import { initiateGoogleLogin } from '../services/auth/GoogleLogin';
import { Button } from './widgets/Button';
import { Card, CardContent } from './widgets/Card';
import { HlsAudioPlayer } from '../utils/HlsAudioPlayer';


interface AudioSample {
  id: string;
  title: string;
  description: string;
  url: string;
}

const audioSamples: AudioSample[] = [
  {
    id: 'weather',
    title: 'Local Weather',
    description: 'Get personalized weather updates for your exact neighborhood',
    url: 'https://audio.locationstation.net/weather-playlist.m3u8'
  },
  {
    id: 'events',
    title: 'Local Events',
    description: 'Discover exciting events happening right around the corner',
    url: 'https://audio.locationstation.net/events-playlist.m3u8'
  },
  {
    id: 'news',
    title: 'Local News',
    description: 'Stay informed about what matters in your community',
    url: 'https://audio.locationstation.net/news-playlist.m3u8'
  }
];

const LocationStationLanding = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const { setUser } = useUser();
  const [player, setPlayer] = useState<HlsAudioPlayer | null>(null);
  const [, setIsPlaying] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    interest: '',
    background: '',
    comments: '',
  });

  const handlePlayPause = async (sample: { id: string; url: string }) => {
    try {
      if (playingId === sample.id) {
        // Pause current stream
        await player?.pause();
        setPlayingId(null);
        setIsPlaying(false);
      } else {
        // Stop previous stream if exists
        if (player) {
          await player.pause();
          player.destroy();
        }

        // Create new player and start stream
        const newPlayer = new HlsAudioPlayer();
        setPlayer(newPlayer);

        await newPlayer.play(sample.url);
        setPlayingId(sample.id);
        setIsPlaying(true);

        // Handle stream end
        newPlayer.addEventListener('ended', () => {
          setPlayingId(null);
          setIsPlaying(false);
          newPlayer.destroy();
          setPlayer(null);
        });
      }
    } catch (error) {
      console.error('Playback error:', error);
      setPlayingId(null);
      setIsPlaying(false);
      player?.destroy();
      setPlayer(null);
    }
  };

  useEffect(() => {
    return () => {
      player?.destroy();
    };
  }, [player]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/eap`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      console.log(response);
      alert('Thank you for your interest! Please check your email for confirmation.');
      setFormData({ name: '', email: '', interest: '', background: '', comments: '' }); // Reset form
    } catch (error) {
      console.error('Form submission error:', error);
      alert('An error occurred while submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const googleUser = await initiateGoogleLogin();
      if (googleUser) {
        axios.get(`${process.env.REACT_APP_API_URL}/user`,
          {
            withCredentials: true,
            params: {
              forceUser: JSON.parse(process.env.REACT_APP_USER || '{}')?.email,
            }
          }
        ).then((res) => {
          const user: User = res.data;
          setUser(user);
        })
          .catch((error) => {
            if (error.response) {
              // Server responded with error status
              console.error('Server Error:', error.response.status, error.response.data);
              // Handle specific status codes
              switch (error.response.status) {
                case 401:
                  alert(`Be sure to sign in with a Google account that has been given access to the Early Access Program for Location Station. If you don't have access, you can request access using the form below.`);
                  setUser(null); // Clear user on unauthorized
                  break;
                case 404:
                  alert(`Be sure to sign in with a Google account that has been given access to the Early Access Program for Location Station. If you don't have access, you can request access using the form below.`);
                  console.error('User not found');
                  break;
                default:
                  console.error('Server error occurred');
              }
            } else if (error.request) {
              // Request made but no response received
              console.error('Network Error:', error.request);
            } else {
              // Error in request configuration
              console.error('Request Error:', error.message);
            }

            // Set error state if you have one
            // setError(error.message);

            // Optionally set user to null on any error
            setUser(null);
          });
      }
    } catch (error) {
      alert(`Be sure to sign in with a Google account that has been given access to the Early Access Program for Location Station. If you don't have access, you can request access using the form below.`);
      console.error('Login failed:', error);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-b from-blue-50 to-white flex justify-center">
      <div className="w-full">
        <div id="should-be-scrollable" className="mx-auto px-4 py-12 h-screen overflow-y-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <img src={BannerImage} alt='Location Station logo' />
            </div>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              Your AI-powered hyperlocal radio station that keeps you connected to what matters
              most in your community. Personalized news, weather, and events, delivered in a
              seamless radio experience.
            </p>
            <p className="text-sm text-gray-700 mb-4">
              <strong>Heads up:</strong> Audio will begin playing as soon as you sign in. Please check your volume.
            </p>
            <img
              src={SigninWithGoogle}
              alt="Google logo"
              onClick={handleGoogleSignIn}
              className="cursor-pointer hover:opacity-80 transition-opacity mx-auto"
            />

          </div>

          {/* Sample Audio Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {audioSamples.map((sample) => (
              <Card key={sample.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {sample.title}
                      </h3>
                      <p className="text-gray-600">{sample.description}</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full mt-4"
                    onClick={() => handlePlayPause(sample)}
                  >
                    {playingId === sample.id ? (
                      <>
                        <Pause className="w-5 h-5 mr-2" />
                        Stop Preview
                      </>
                    ) : (
                      <>
                        <Play className="w-5 h-5 mr-2" />
                        Play Preview
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Features */}
          <div className="mt-16 text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Location Station?
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="p-4">
                <h3 className="font-semibold mb-2">Hyperlocal Focus</h3>
                <p className="text-gray-600">Content that matters to your neighborhood</p>
              </div>
              <div className="p-4">
                <h3 className="font-semibold mb-2">AI-Powered</h3>
                <p className="text-gray-600">Personalized to your interests</p>
              </div>
              <div className="p-4">
                <h3 className="font-semibold mb-2">Always Fresh</h3>
                <p className="text-gray-600">Real-time updates throughout the day</p>
              </div>
            </div>
          </div>

          {/* Early Access Form */}
          <div className="mt-16 text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Join Our Early Access Program
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto mb-8">
              We'd love to hear from you! Share your interest and become part of the Location Station journey.
            </p>
            <form onSubmit={handleFormSubmit} className="max-w-lg mx-auto">
              <div className="mb-4">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Full Name"
                  value={formData.name}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-lg p-3"
                  required
                />
              </div>
              <div className="mb-4">
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email Address"
                  value={formData.email}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-lg p-3"
                  required
                />
              </div>
              <div className="mb-4">
                <textarea
                  name="interest"
                  placeholder="Why are you interested in Location Station?"
                  value={formData.interest}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-lg p-3"
                  required
                ></textarea>
              </div>
              <div className="mb-4">
                <textarea
                  name="background"
                  placeholder="Tell us about your background (Optional)"
                  value={formData.background}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-lg p-3"
                ></textarea>
              </div>
              <div className="mb-4">
                <textarea
                  name="comments"
                  placeholder="Additional comments or questions (Optional)"
                  value={formData.comments}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-lg p-3"
                ></textarea>
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-blue-600 text-white py-3 rounded-lg ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
                  }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </form>
          </div>

          {/* Footer */}
          <footer className="w-full bg-gray-100 py-4 mt-8 border-t border-gray-300">
            <div className="max-w-4xl mx-auto flex justify-center space-x-6">
              <a
                href="https://html.locationstation.net/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Privacy Policy
              </a>
              <a
                href="https://html.locationstation.net/tos"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Terms of Service
              </a>
              <a
                href="mailto:support@locationstation.net"
                title="support@locationstation.net"
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Contact
              </a>
            </div>
          </footer>
        </div>
      </div>

    </div>
  );
};

export default LocationStationLanding;